import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { 
  IconButton, 
  CircularProgress, 
  Typography, 
  Box 
} from "@material-ui/core";
import { 
  ZoomIn, 
  PlayArrow, 
  GetApp, 
  LocationOn, 
  ContactPhone,
  Error,
  ImageOutlined,
  VideoLibraryOutlined,
  AttachFileOutlined
} from "@material-ui/icons";

// 🎯 Componentes de mídia específicos importados
import WhatsAppAudioPlayer from "./WhatsAppAudioPlayer";
import LocationPreview from "../../LocationPreview";
import VcardPreview from "../../VcardPreview";
import ModalImageCors from "../../ModalImageCors";
import AdvancedMediaViewer from "./AdvancedMediaViewer";

// 🎨 Estilos
import { useMediaStyles } from "../styles/mediaStyles";

// 🎯 Constantes
import { MESSAGE_MEDIA_TYPES } from "../constants/messageConstants";
import { parseLocationMessage, parseContactMessage } from "../utils/messageFormatters";

/**
 * 🔧 Normaliza URLs de mídia para dev/prod
 * - Dev: ajusta localhost:443 -> 8080
 * - Prod: ancora /public no mesmo host e promove http->https em páginas https
 */
const normalizeMediaUrl = (url) => {
  if (!url) return url;
  let u = url;
  if (u.includes('localhost:443/')) {
    u = u.replace('localhost:443/', 'localhost:8080/');
  }
  try {
    const loc = window.location;
    const isHttpsPage = loc.protocol === 'https:';
    const urlObj = new URL(u, loc.origin);
    if (u.startsWith('/public/')) {
      urlObj.protocol = loc.protocol;
      urlObj.host = loc.host;
    }
    if (isHttpsPage && urlObj.protocol === 'http:') {
      urlObj.protocol = 'https:';
    }
    return urlObj.toString();
  } catch {
    return u;
  }
};

const FILE_PREFIX_REGEX = /^(\d{10,})([_-])(.*)$/;

const normalizeFileDisplayName = (value) => {
  if (!value) return "";
  const raw = `${value}`.trim();
  if (!raw) return "";
  const match = raw.match(FILE_PREFIX_REGEX);
  const cleaned = match && match[3] ? match[3] : raw;
  return cleaned.replace(/_/g, " ").replace(/\s+/g, " ").trim();
};

/**
 * 🖼️ Componente para renderizar imagens
 */
export const ImageMedia = ({ 
  message, 
  onError, 
  variant = "normal", // 🎯 Nova prop para contexto
  size = "medium",     // 🎯 Nova prop para tamanho
  buildImageSequence,
}) => {
  const classes = useMediaStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showAdvancedViewer, setShowAdvancedViewer] = useState(false);

  // 🎯 Configurações baseadas no contexto
  const isQuoted = variant === "quoted";
  const isPreview = variant === "preview";
  const showOverlay = !isQuoted && !isPreview;
  const showDownloadChip = !isPreview;

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
  }, []);
  const handleImageError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
    if (onError) {
      onError();
    }
  }, [onError]);

  const [gallery, setGallery] = useState({ items: null, startIndex: 0 });

  const handleImageClick = useCallback(() => {
    if (typeof buildImageSequence === 'function') {
      const seq = buildImageSequence(message.id);
      if (seq?.items?.length) {
        setGallery(seq);
      }
    }
    setShowAdvancedViewer(true);
  }, [buildImageSequence, message?.id]);

  const handleDownload = useCallback(() => {
  const correctedUrl = normalizeMediaUrl(message.mediaUrl);
    
    // Método mais robusto para download
    fetch(correctedUrl)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = message.filename || 'imagem.jpg';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Erro ao baixar imagem:', error);
        // Fallback para o método anterior
        const link = document.createElement('a');
        link.href = correctedUrl;
        link.download = message.filename || 'imagem.jpg';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }, [message.mediaUrl, message.filename]);

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
  const correctedUrl = normalizeMediaUrl(message.mediaUrl);
        await navigator.share({
          title: 'Imagem compartilhada',
          url: correctedUrl,
        });
      } catch (error) {
        console.error('Erro ao compartilhar:', error);
      }
    }
  }, [message.mediaUrl]);

  if (hasError) {
    return (
      <Box className={classes.errorContainer}>
        <Error style={{ marginRight: 8 }} />
        <Typography variant="body2">Erro ao carregar imagem</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Container condicional - apenas para contexto normal */}
      {!isQuoted && !isPreview && (
        <div className={classes.mediaContainer}>
          <div 
            className={clsx(classes.imageContainer)}
            onClick={showOverlay ? handleImageClick : undefined}
            role={showOverlay ? "button" : undefined}
            tabIndex={showOverlay ? 0 : undefined}
            onKeyPress={showOverlay ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleImageClick();
              }
            } : undefined}
            style={isLoading ? { minHeight: 150 } : undefined}
          >
            {isLoading && (
              <Box className={classes.loadingContainer}>
                <CircularProgress size={24} className={classes.loadingSpinner} />
              </Box>
            )}
            <img
              className={clsx(classes.mediaImage)}
              src={normalizeMediaUrl(message.mediaUrl)}
              alt="Imagem da mensagem"
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ display: isLoading ? 'none' : 'block' }}
            />
            {showOverlay && (
              <div className={classes.imageOverlay}>
                <ZoomIn className={classes.zoomIcon} />
              </div>
            )}
            
            {/* 💾 Botão de download hover - apenas em contexto normal */}
            {showDownloadChip && (
              <div 
                className={classes.downloadChip}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
              >
                <GetApp className={classes.downloadChipIcon} />
                Download
              </div>
            )}
          </div>
        </div>
      )}

      {/* Container direto para quoted/preview */}
      {(isQuoted || isPreview) && (
        <div 
          className={clsx(classes.imageContainer, {
            [classes.quotedImageContainer]: isQuoted,
            [classes.previewImageContainer]: isPreview,
          })}
          onClick={showOverlay ? handleImageClick : undefined}
          role={showOverlay ? "button" : undefined}
          tabIndex={showOverlay ? 0 : undefined}
          onKeyPress={showOverlay ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleImageClick();
            }
          } : undefined}
        >
          {isLoading && (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={isPreview ? 16 : 24} className={classes.loadingSpinner} />
            </Box>
          )}
          <img
            className={clsx(classes.mediaImage, {
              [classes.quotedImage]: isQuoted,
              [classes.previewImage]: isPreview,
            })}
            src={normalizeMediaUrl(message.mediaUrl)}
            alt="Imagem da mensagem"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: isLoading ? 'none' : 'block' }}
          />
        </div>
      )}

      {/* Modal avançado apenas em contexto normal */}
      {!isPreview && (
        <AdvancedMediaViewer
          open={showAdvancedViewer}
          onClose={() => setShowAdvancedViewer(false)}
          mediaUrl={normalizeMediaUrl(message.mediaUrl)}
          mediaType="image"
          title={message.filename || 'Imagem'}
          onDownload={handleDownload}
          onShare={navigator.share ? handleShare : null}
          showDownloadButton={false} // Oculto pois já tem o chip hover
          items={gallery.items?.map(it => ({ ...it, url: normalizeMediaUrl(it.url) })) || null}
          currentIndex={gallery.startIndex || 0}
        />
      )}
    </>
  );
};

/**
 * 🎥 Componente para renderizar vídeos
 */
export const VideoMedia = ({ 
  message, 
  onError, 
  variant = "normal", // 🎯 Nova prop para contexto
  size = "medium"     // 🎯 Nova prop para tamanho
}) => {
  const classes = useMediaStyles();
  const [hasError, setHasError] = useState(false);
  const [showAdvancedViewer, setShowAdvancedViewer] = useState(false);
  
  // 🎯 Configurações baseadas no contexto
  const isQuoted = variant === "quoted";
  const isPreview = variant === "preview";
  const showOverlay = !isQuoted && !isPreview;
  const showDownloadChip = !isPreview;
  const handleVideoError = useCallback(() => {
    setHasError(true);
    if (onError) {
      onError();
    }
  }, [onError]);

  const handleVideoClick = useCallback(() => {
    setShowAdvancedViewer(true);
  }, []);

  const handleDownload = useCallback(() => {
  const correctedUrl = normalizeMediaUrl(message.mediaUrl);
    
    // Método mais robusto para download
    fetch(correctedUrl)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = message.filename || 'video.mp4';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Erro ao baixar vídeo:', error);
        // Fallback para o método anterior
        const link = document.createElement('a');
        link.href = correctedUrl;
        link.download = message.filename || 'video.mp4';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }, [message.mediaUrl, message.filename]);

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
  const correctedUrl = normalizeMediaUrl(message.mediaUrl);
        await navigator.share({
          title: 'Vídeo compartilhado',
          url: correctedUrl,
        });
      } catch (error) {
        console.error('Erro ao compartilhar:', error);
      }
    }
  }, [message.mediaUrl]);

  if (hasError) {
    return (
      <Box className={classes.errorContainer}>
        <Error style={{ marginRight: 8 }} />
        <Typography variant="body2">Erro ao carregar vídeo</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Container condicional - apenas para contexto normal */}
      {!isQuoted && !isPreview && (
        <div className={classes.mediaContainer}>
          <div 
            className={clsx(classes.videoContainer)}
            onClick={showOverlay ? handleVideoClick : undefined}
            role={showOverlay ? "button" : undefined}
            tabIndex={showOverlay ? 0 : undefined}
            onKeyPress={showOverlay ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleVideoClick();
              }
            } : undefined}
          >
            <video
              className={clsx(classes.mediaVideo)}
              src={normalizeMediaUrl(message.mediaUrl)}
              preload="metadata"
              onError={handleVideoError}
              poster={message.thumbnailUrl ? normalizeMediaUrl(message.thumbnailUrl) : undefined}
              muted
              playsInline
              controls={false}
            >
              <track kind="captions" />
              Seu navegador não suporta o elemento de vídeo.
            </video>
            
            {/* ▶️ Overlay com botão play - apenas em contexto normal */}
            {showOverlay && (
              <div className={classes.videoOverlay}>
                <div className={classes.playButton}>
                  <PlayArrow className={classes.playIcon} />
                </div>
              </div>
            )}
            
            {/* 💾 Botão de download hover - apenas em contexto normal */}
            {showDownloadChip && (
              <div 
                className={classes.downloadChip}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
              >
                <GetApp className={classes.downloadChipIcon} />
                Download
              </div>
            )}
          </div>
        </div>
      )}

      {/* Container direto para quoted/preview */}
      {(isQuoted || isPreview) && (
        <div 
          className={clsx(classes.videoContainer, {
            [classes.quotedVideoContainer]: isQuoted,
            [classes.previewVideoContainer]: isPreview,
          })}
          onClick={showOverlay ? handleVideoClick : undefined}
          role={showOverlay ? "button" : undefined}
          tabIndex={showOverlay ? 0 : undefined}
          onKeyPress={showOverlay ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleVideoClick();
            }
          } : undefined}
        >
          <video
            className={clsx(classes.mediaVideo, {
              [classes.quotedVideo]: isQuoted,
              [classes.previewVideo]: isPreview,
            })}
            src={normalizeMediaUrl(message.mediaUrl)}
            preload="metadata"
            onError={handleVideoError}
            poster={message.thumbnailUrl ? normalizeMediaUrl(message.thumbnailUrl) : undefined}
            muted
            playsInline
            controls={false}
          >
            <track kind="captions" />
            Seu navegador não suporta o elemento de vídeo.
          </video>
          
          {/* ▶️ Pequeno ícone de play para quoted/preview */}
          {(isQuoted || isPreview) && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              borderRadius: '50%',
              width: isPreview ? '20px' : '24px',
              height: isPreview ? '20px' : '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}>
              <PlayArrow style={{ 
                color: 'white', 
                fontSize: isPreview ? '12px' : '14px',
                marginLeft: '1px'
              }} />
            </div>
          )}
        </div>
      )}

      {/* Modal avançado apenas em contexto normal */}
      {!isPreview && (
        <AdvancedMediaViewer
          open={showAdvancedViewer}
          onClose={() => setShowAdvancedViewer(false)}
          mediaUrl={normalizeMediaUrl(message.mediaUrl)}
          mediaType="video"
          title={message.filename || 'Vídeo'}
          onDownload={handleDownload}
          onShare={navigator.share ? handleShare : null}
          showDownloadButton={false} // Oculto pois já tem o chip hover
          showZoomControls={false} // Não precisa de zoom para vídeo
          showRotateControls={false} // Não precisa de rotação para vídeo
          showVolumeControls={true} // Importante para vídeos
        />
      )}
    </>
  );
};

/**
 * 🎵 Componente para renderizar áudios - Estilo WhatsApp moderno
 */
export const AudioMedia = ({ message, messageIndex = -1 }) => {
  return <WhatsAppAudioPlayer message={message} messageIndex={messageIndex} />;
};

/**
 * 📎 Componente para renderizar arquivos
 */
export const FileMedia = ({ message }) => {
  const classes = useMediaStyles();

  const getFileExtension = (filename, mimetype) => {
    if (filename && filename.includes('.')) {
      const ext = filename.split('.').pop();
      if (ext) {
        return ext.toUpperCase();
      }
    }
    if (mimetype && mimetype.includes('/')) {
      const extFromMime = mimetype.split('/').pop();
      if (extFromMime) {
        return extFromMime.toUpperCase();
      }
    }
    return "";
  };

  const rawName = message.filename || message.originalName || message.body;
  const displayName = normalizeFileDisplayName(rawName) || "Arquivo";
  const fileExtension = getFileExtension(rawName, message.mimetype);

  const openFile = () => {
    window.open(normalizeMediaUrl(message.mediaUrl), "_blank");
  };

  return (
    <div className={classes.mediaContainer}>
      <div
        className={classes.fileContainer}
        onClick={openFile}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            openFile();
          }
        }}
      >
        <AttachFileOutlined className={classes.fileIcon} />
        <div className={classes.fileInfo}>
          <Typography className={classes.fileName} title={displayName}>
            {displayName}
          </Typography>
          {fileExtension && (
            <Typography className={classes.fileType}>
              {fileExtension}
            </Typography>
          )}
        </div>
        <IconButton
          className={classes.downloadButton}
          size="small"
          aria-label={`Download ${displayName}`}
          onClick={(event) => {
            event.stopPropagation();
            openFile();
          }}
        >
          <GetApp />
        </IconButton>
      </div>
    </div>
  );
};

/**
 * 📍 Componente para renderizar localização
 */
export const LocationMedia = ({ message }) => {
  const classes = useMediaStyles();
  
  if (!message.body || message.body.split("|").length < 2) {
    return (
      <Box className={classes.errorContainer}>
        <LocationOn style={{ marginRight: 8 }} />
        <Typography variant="body2">Localização inválida</Typography>
      </Box>
    );
  }

  const { imageLocation, linkLocation, descriptionLocation } = parseLocationMessage(message.body);

  return (
    <div className={classes.mediaContainer}>
      <div className={classes.locationContainer}>
        <LocationPreview
          image={imageLocation}
          link={linkLocation}
          description={descriptionLocation}
        />
      </div>
    </div>
  );
};

/**
 * 📇 Componente para renderizar contatos
 */
export const ContactMedia = ({ message, whatsappId, queueId, variant = 'normal', size = 'normal' }) => {
  const classes = useMediaStyles();
  
  const isQuoted = variant === 'quoted';
  const isPreview = variant === 'preview';
  
  if (!message.body) {
    return (
      <Box className={classes.errorContainer}>
        <ContactPhone style={{ marginRight: 8 }} />
        <Typography variant="body2">Contato inválido</Typography>
      </Box>
    );
  }

  const { contact, numbers } = parseContactMessage(message.body);

  // Versão simplificada para quoted/preview
  if (isQuoted || isPreview) {
    return (
      <div className={clsx(classes.contactContainer, {
        [classes.quotedContactContainer]: isQuoted,
        [classes.previewContactContainer]: isPreview,
      })}>
        <ContactPhone className={classes.contactIcon} />
        <div className={classes.contactInfo}>
          <Typography className={classes.contactName} variant="body2">
            {contact || 'Contato'}
          </Typography>
          {numbers && (
            <Typography className={classes.contactNumber} variant="caption">
              {numbers}
            </Typography>
          )}
        </div>
      </div>
    );
  }

  // Versão completa para contexto normal
  return (
    <div className={classes.mediaContainer}>
      <div className={classes.contactContainer}>
        <VcardPreview 
          contact={contact} 
          numbers={numbers} 
          queueId={queueId}
          whatsappId={whatsappId}
        />
      </div>
    </div>
  );
};

/**
 * 🚫 Componente para tipos de mídia não suportados
 */
export const UnsupportedMedia = ({ mediaType }) => {
  const classes = useMediaStyles();

  return (
    <Box className={classes.errorContainer}>
      <Error style={{ marginRight: 8 }} />
      <Typography variant="body2">
        Tipo de mídia não suportado: {mediaType}
      </Typography>
    </Box>
  );
};

// PropTypes
const mediaPropTypes = {
  message: PropTypes.shape({
    mediaType: PropTypes.string,
    mediaUrl: PropTypes.string,
    body: PropTypes.string,
    filename: PropTypes.string,
    originalName: PropTypes.string,
    duration: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    mimetype: PropTypes.string,
  }).isRequired,
  onError: PropTypes.func,
};

ImageMedia.propTypes = mediaPropTypes;
VideoMedia.propTypes = mediaPropTypes;
AudioMedia.propTypes = mediaPropTypes;
FileMedia.propTypes = mediaPropTypes;
LocationMedia.propTypes = mediaPropTypes;
ContactMedia.propTypes = mediaPropTypes;

UnsupportedMedia.propTypes = {
  mediaType: PropTypes.string.isRequired,
};

export default {
  ImageMedia,
  VideoMedia,
  AudioMedia,
  FileMedia,
  LocationMedia,
  ContactMedia,
  UnsupportedMedia,
};
